// Mirrors of the Go HTTP DTOs. Field names match the JSON tags on the Go side
// exactly so they can be passed through `JSON.parse` without remapping.

export interface Tenant {
  id: number;
  amocrm_domain: string;
  amocrm_account_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export type Role = "user" | "assistant" | "error";

// WorkspaceRole is the user's authority within their workspace (tenant).
export type WorkspaceRole = "owner" | "member";

// UserTenant is one workspace the current user belongs to (an entry of the
// account switcher), with their role there.
export interface UserTenant {
  id: number;
  name: string;
  role: WorkspaceRole;
}

// CurrentUser is the payload of GET /api/me — used to render "My/Shared" tabs
// (id), gate owner-only UI (role), and populate the account switcher
// (tenants). tenant_id is the ACTIVE workspace (0 = none); role is the user's
// role in it ("" when tenant_id is 0).
export interface CurrentUser {
  id: number;
  email: string;
  role: WorkspaceRole | "";
  tenant_id: number;
  tenants: UserTenant[];
}

// Member mirrors the members list DTO from GET /api/members.
export interface Member {
  id: number;
  email: string;
  role: WorkspaceRole;
  email_confirmed: boolean;
  created_at: string;
}

export interface ChatSession {
  id: number;
  tenant_id: number;
  owner_user_id?: number | null;
  // report_id binds an editor chat to the single report it edits; null/absent
  // for a standalone (general) chat.
  report_id?: number | null;
  name?: string;
  created_at: string;
  updated_at: string;
}

export interface PivotConfig {
  rowDimensions: string[];
  columnDimensions: string[];
  metrics: string[];
  metricsPosition?: string;
  metricFormats?: Record<string, "integer" | "decimal" | "percent" | "currency" | "duration">;
}

export type FilterType = "multiselect" | "singleselect" | "daterange";

// Mirror of chat.FilterSpec. `column`/`options_sql` are backend-only details
// the UI doesn't need, but they ride along on the report DTO (like frozen_sql);
// the UI keys off `id`, `label`, and `type`.
export interface ReportFilter {
  id: string;
  label: string;
  type: FilterType;
  column?: string;
  options_sql?: string;
}

// One selectable value for an option-backed filter (GET .../filters/{id}/options).
export interface FilterOption {
  value: string;
  label: string;
}

// Selected filter values sent on run: filter id -> chosen values. For a
// singleselect has one item; daterange is [from, to].
export type FilterValues = Record<string, string[]>;

export interface AgentTableJSON {
  sql?: string;
  role?: "primary" | "supporting";
  columns: string[];
  rows: unknown[][];
}

export interface AgentStepJSON {
  tool_name: string;
  tool_input?: Record<string, unknown>;
  duration_ms: number;
  is_error?: boolean;
}

export interface AgentResponsePayload {
  stop_reason: string;
  text?: string;
  tables?: AgentTableJSON[];
  steps?: AgentStepJSON[];
  pivot?: PivotConfig | null;
  filters?: ReportFilter[];
  clarifying_question?: string;
  result_columns?: string[];
  result_rows?: unknown[][];
}

export interface ChatMessage {
  id: number;
  session_id: number;
  tenant_id: number;
  role: Role;
  content: string;
  generated_sql?: string;
  date_from?: string;
  date_to?: string;
  skill?: string;
  payload?: AgentResponsePayload;
  created_at: string;
}

// SessionDetailResponse deliberately carries no run state: the single source
// of truth for a session's agent run is GET /api/chat/sessions/{id}/stream,
// which the client opens whenever it displays the session (204 = nothing
// running).
export interface SessionDetailResponse {
  session: ChatSession;
  messages: ChatMessage[];
}

export interface SyncEntityProgress {
  status: string;
  count: number;
}

export interface SyncStatusResponse {
  status: "pending" | "running" | "completed" | "failed" | string;
  type?: string;
  records_fetched?: number;
  error_message?: string;
  duration_seconds?: number;
  completed_at?: string;
  progress?: Record<string, SyncEntityProgress>;
}

// SSE event from GET /api/chat/sessions/{id}/stream (the run's event log).
// Mirrors chat.AgentEvent in the Go backend.
export type AgentEventType =
  | "step_start"
  | "step_done"
  | "clarify"
  | "done"
  | "error"
  | "cancelled";

export type Visibility = "private" | "public";

export interface Report {
  id: number;
  tenant_id: number;
  name: string;
  // The report's self-contained definition, copied from the chat message it was
  // created from. No "frozen" reconciliation, no chat dependency.
  sql: string;
  view: PivotConfig | null;
  filters?: ReportFilter[] | null;
  date_from?: string | null;
  date_to?: string | null;
  owner_user_id?: number | null;
  visibility: Visibility;
  // version is the optimistic-concurrency stamp. Echo it back on save as
  // expected_version; a mismatch returns 409 version_conflict with the
  // current_version in the error body. Bumped only on definition saves —
  // rename / publish keep the version stable.
  version: number;
  created_at: string;
  updated_at: string;
  // is_favorite is the CURRENT USER's star on this report (per-user bookmark).
  // Reliable only on the list endpoint; single-report responses omit it.
  is_favorite?: boolean;
}

export interface ReportRunResult {
  columns: string[];
  rows: unknown[][];
  total_rows: number;
  page: number;
  page_size: number;
  total_pages: number;
  used_date_from?: string | null;
  used_date_to?: string | null;
  view?: PivotConfig | null;
}

export interface AgentEvent {
  type: AgentEventType;
  step_idx?: number;
  tool_name?: string;
  tool_input?: Record<string, unknown>;
  duration_ms?: number;
  is_error?: boolean;
  question?: string;
  payload?: AgentResponsePayload;
  error?: string;
}

export interface ApiErrorBody {
  error_code?: string;
  error?: string;
  params?: Record<string, unknown>;
  // current_version is set on 409 version_conflict (POST /api/reports/{id}/save).
  // The client uses it to reload and retry without an extra GET.
  current_version?: number;
}

export class ApiError extends Error {
  status: number;
  code: string;
  params?: Record<string, unknown>;
  // currentVersion: only set when code === "version_conflict".
  currentVersion?: number;

  constructor(status: number, body: ApiErrorBody, fallback = "request_failed") {
    const code = body.error_code ?? body.error ?? fallback;
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.params = body.params;
    this.currentVersion = body.current_version;
  }
}
